import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { pricingAPI } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/CarPrice.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';

function CarMultiPrice() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [form, setForm] = useState({
    from_dis: '',
    to_dis: '',
    price_vehicle1: '',
    price_vehicle2: '',
    price_vehicle3: '',
    price_vehicle4: '',
    discount_percentage: ''
  });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const CarsName = [
    { 
      name: 'Caddy'
    },
    { 
      name: 'Bus'
    },
    { 
      name: 'Maxi Bus'
    },
    { 
      name: 'Planenbus'
    },
  ]

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.multiDistances.index({
        page,
        per_page: pagination.per_page
      });

      if (response.success) {
        setData(response.data.data || response.data);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.length
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      setForm({
        from_dis: entry.from_dis || '',
        to_dis: entry.to_dis || '',
        price_vehicle1: entry.price_vehicle1 || '',
        price_vehicle2: entry.price_vehicle2 || '',
        price_vehicle3: entry.price_vehicle3 || '',
        price_vehicle4: entry.price_vehicle4 || '',
        discount_percentage: entry.discount_percentage || ''
      });
      setEditData(entry);
      setHighlightedId(entry.id);
    } else {
      setForm({
        from_dis: '',
        to_dis: '',
        price_vehicle1: '',
        price_vehicle2: '',
        price_vehicle3: '',
        price_vehicle4: '',
        discount_percentage: ''
      });
      setEditData(null);
      setHighlightedId(null);
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        fd.append(key, value);
      });

      let response;
      if (editData) {
        response = await pricingAPI.multiDistances.update(editData.id, fd);
      } else {
        response = await pricingAPI.multiDistances.store(fd);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page); // Refresh current page
        if (editData) {
          setHighlightedId(editData.id);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.multiDistances.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowDelete(false);
        fetchData(pagination.current_page); // Refresh current page
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_delete_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_delete_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error deleting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  return (
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title">🚚{t('Preis_multi_entfernung')}</h3>
          <Button className='add-btn' onClick={() => openForm()}>
            <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
          </Button>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : data.length === 0 ? (
          <Alert variant="info">{t('no_data_available')}</Alert>
        ) : (
          <div className="table-container shadow-sm rounded">
            <div className='table-responsive'>
              <Table bordered hover className="custom-table mb-0">
                <thead className="table-dark text-center">
                  <tr>
                    <th>#</th>
                    <th>{t("from")}</th>
                    <th>{t("to")}</th>
                    {/* <th>{t("Caddy")}</th>
                    <th>{t("Bus")}</th>
                    <th>{t("Maxi Bus")}</th>
                    <th>{t("Planenbus")}</th> */}
                    {CarsName.map(({name}, i) => (
                      <th key={`s${i}`}>{name}</th>
                    ))}
                    <th>{t("coupons.discount_percentage")}</th>
                    <th>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry, idx) => (
                    <tr key={entry.id} className={`text-center ${highlightedId === entry.id ? 'row-highlighted' : ''}`}>
                      <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                      <td>{entry.from_dis}</td>
                      <td>{entry.to_dis}</td>
                      <td>{entry.price_vehicle1}</td>
                      <td>{entry.price_vehicle2}</td>
                      <td>{entry.price_vehicle3}</td>
                      <td>{entry.price_vehicle4}</td>
                      <td>{entry.discount_percentage}%</td>
                      <td>
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => openForm(entry)}
                          title="Bearbeiten"
                          disabled={loading}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteId(entry.id);
                            setShowDelete(true);
                          }}
                          title={t("Delete")}
                          disabled={loading}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {!loading && pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchData}
            disabled={loading}
          />
        )}


      {/* Add/Edit Modal */}
      <Modal show={showForm} 
        onHide={() => {
          setShowForm(false);
          if (editData) {
            setHighlightedId(editData.id);
          }
        }} 
        size="xl" 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editData ? 'Bearbeiten' : 'Hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("from")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.from_dis}
                    onChange={e => setForm(prev => ({ ...prev, from_dis: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("to")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.to_dis}
                    onChange={e => setForm(prev => ({ ...prev, to_dis: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* {("city")} Prices */}
            <h6 className="mt-4 mb-3">{t("car_prices")}</h6>
            <Row className="mb-3 gy-2">
              {CarsName.map(({name}, i) => (
                <Col md={6} key={`city-${i + 1}`}>
                  <Form.Group>
                    <Form.Label>{name}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={form[`price_vehicle${i + 1}`]}
                      onChange={e => setForm(prev => ({ ...prev, [`price_vehicle${i + 1}`]: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              ))}
            </Row>

            {/* discount percentage */}
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("coupons.discount_percentage")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.discount_percentage}
                    onChange={e => setForm(prev => ({ ...prev, discount_percentage: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading || !form.from_dis || !form.to_dis}>
            {loading ? t('Saving...') : t("save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_entry")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('deleting') : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CarMultiPrice;